/**
 * Step 4: the value simulation — a labelled, stepwise preview of the exact
 * milestones the real dashboard will later fill with data.
 *
 * Everything on screen is either the merchant's own product (title, REAL
 * price) or a stage name that maps 1:1 to a real metric. No uplift, no
 * conversion rate, no visitor counts, no forecast — and the disclaimer is
 * rendered prominently, always, in both locales.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Icon,
  InlineStack,
  Text,
} from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import { useLanguage } from '../providers/LanguageProvider';
import { formatPrice } from './format';

/** ms between each simulated milestone appearing. */
export const SIMULATION_STEP_INTERVAL_MS = 900;

export interface SimulationProduct {
  title: string;
  price: string | null;
  currencyCode: string | null;
}

interface SimulationStepProps {
  product: SimulationProduct;
  onContinue: () => void;
  onBack?: (() => void) | null;
  /** Compact mode for the replay modal on the real dashboard. */
  embedded?: boolean;
}

export default function SimulationStep({ product, onContinue, onBack, embedded = false }: SimulationStepProps) {
  const { t, language } = useLanguage();
  const o = t.onboardingFlow;
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [revealed, setRevealed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const play = useCallback(() => {
    setRevealed(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRevealed((count) => {
        if (count >= 4) {
          if (timerRef.current) clearInterval(timerRef.current);
          return count;
        }
        return count + 1;
      });
    }, SIMULATION_STEP_INTERVAL_MS);
  }, []);

  useEffect(() => {
    play();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [play]);

  const price = formatPrice(product.price, product.currencyCode, locale);
  const stages = [
    { label: o.simStepTryon, value: null },
    { label: o.simStepCart, value: null },
    { label: o.simStepOrder, value: null },
    { label: o.simStepRevenue, value: price },
  ];

  return (
    <Card>
      <BlockStack gap="400">
        {!embedded && (
          <BlockStack gap="100">
            <Text as="h2" variant="headingLg">
              {o.simTitle}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              {o.simSubtitle}
            </Text>
          </BlockStack>
        )}

        <Banner tone="warning">
          <Text as="p" fontWeight="semibold">
            {o.simDisclaimer}
          </Text>
        </Banner>

        <BlockStack gap="200">
          {stages.map((stage, index) => {
            const shown = index < revealed;
            return (
              <Box
                key={stage.label}
                background={shown ? 'bg-surface-success' : 'bg-surface-secondary'}
                borderRadius="200"
                padding="300"
              >
                <InlineStack gap="200" blockAlign="center" align="space-between">
                  <InlineStack gap="200" blockAlign="center">
                    <span
                      style={{
                        opacity: shown ? 1 : 0.25,
                        transition: 'opacity 300ms ease',
                        display: 'inline-flex',
                      }}
                    >
                      <Icon source={CheckCircleIcon} tone={shown ? 'success' : 'subdued'} />
                    </span>
                    <Text as="span" variant="bodyMd" fontWeight={shown ? 'semibold' : 'regular'}>
                      {index + 1}. {stage.label}
                    </Text>
                  </InlineStack>
                  {stage.value && shown && (
                    <Text as="span" variant="headingMd">
                      {stage.value}
                    </Text>
                  )}
                </InlineStack>
              </Box>
            );
          })}
        </BlockStack>

        <InlineStack gap="200" blockAlign="center">
          <Badge tone="info">{product.title}</Badge>
          <Text as="span" variant="bodySm" tone="subdued">
            {o.simRevenueNote}
          </Text>
        </InlineStack>

        <InlineStack gap="300" wrap>
          <Button variant="primary" size="large" onClick={embedded ? play : onContinue}>
            {embedded ? o.simReplay : o.simCta}
          </Button>
          {!embedded && (
            <Button variant="plain" onClick={play}>
              {o.simReplay}
            </Button>
          )}
          {!embedded && onBack && (
            <Button variant="plain" onClick={onBack}>
              {o.simBack}
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
